import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { describeError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, PlayCircle, PuzzlePiece } from "@phosphor-icons/react";
import { JangleBrand } from "@/components/JangleLogo";
import Avatar2D from "@/components/Avatar2D";
import { CHARACTERS } from "@/lib/avatarConfig";

// "Raider arena" landing page (Jack picked this look on 2026-10-12): the game's
// pink (you) and blue (opponent) tiles, two raiders facing off, and the full
// raider line-up. The old "Choose your battlefield" board list is gone; Fortress
// is the only board.

const YOU_FLAG = "#f393a1";
const OPP_FLAG = "#4FB0F0";
const BY_ID = Object.fromEntries(CHARACTERS.map((c) => [c.id, c]));

const STEPS = [
  { n: "1", title: "Spell", text: "Play words from your pink tiles to grow a chain up the board.", tone: "you" },
  { n: "2", title: "Smash", text: "Land on a POW to blow up their tiles. Steel Shields stand firm.", tone: "opp" },
  { n: "3", title: "Capture", text: "Reach their base and your raider grabs the flag. Capture wins pay 1.5x Elo.", tone: "you" },
];

function GuestLink() {
  const { playAsGuest } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      await playAsGuest();
      navigate("/avatar?welcome=1", { replace: true });
    } catch (err) {
      toast.error(describeError(err));
      setBusy(false);
    }
  };
  return (
    <Button
      variant="ghost"
      size="lg"
      onClick={start}
      disabled={busy}
      className="rounded-xl h-12 px-6 font-semibold text-muted-foreground underline underline-offset-4"
      data-testid="hero-cta-guest"
    >
      {busy ? "Starting…" : "Play as guest"}
    </Button>
  );
}

function Tile({ ch, tone, className = "" }) {
  return <span className={`game-tile ${tone === "opp" ? "game-tile-opp" : "game-tile-you"} ${className}`}>{ch}</span>;
}

export default function Landing() {
  return (
    <main className="bg-background overflow-x-hidden" data-testid="landing-page">
      {/* Hero */}
      <section
        className="relative"
        style={{
          background:
            "radial-gradient(900px 480px at 22% 62%, rgba(243,147,161,.16), transparent 60%), radial-gradient(900px 480px at 78% 62%, rgba(79,176,240,.16), transparent 60%)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-14 text-center animate-fade-up">
          <JangleBrand
            className="mx-auto drop-shadow-[0_18px_30px_rgba(0,0,0,.45)]"
            logoClassName="h-28 sm:h-36 md:h-44"
            sizes="(min-width: 768px) 440px, 300px"
          />

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02] mt-4 text-balance">
            The word game <span className="text-brand">you</span> <span className="text-opp">raid.</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Pick a raider, spell your way across the board and capture your rival's flag. One on one, Elo ranked.
          </p>

          {/* Face-off */}
          <div className="mt-6 flex items-end justify-center gap-0 sm:gap-6" aria-hidden="true">
            <Avatar2D character={BY_ID.barbarian} flag={YOU_FLAG} className="w-36 sm:w-48 md:w-60" />
            <div className="flex flex-col items-center gap-2.5 mb-10 sm:mb-16">
              <div className="flex gap-1.5">
                <Tile ch="V" tone="you" className="w-11 h-11 text-2xl sm:w-16 sm:h-16 sm:text-4xl" />
                <Tile ch="S" tone="opp" className="w-11 h-11 text-2xl sm:w-16 sm:h-16 sm:text-4xl" />
              </div>
              <span className="w-11 h-11 sm:w-14 sm:h-14 rounded-lg bg-[#a9b8a0] flex items-center justify-center">
                <img src="/brand/pow-hammer.svg" alt="" className="w-[86%]" />
              </span>
            </div>
            <Avatar2D character={BY_ID.frost} flag={OPP_FLAG} mirror className="w-36 sm:w-48 md:w-60" />
          </div>

          {/* The front door (Jack, 2026-10-26): today's mini puzzle, no sign-up. */}
          <div className="mt-5 flex justify-center">
            <Link
              to="/mini"
              className="group inline-flex items-center gap-3 rounded-2xl bg-opp text-white px-6 sm:px-8 py-3.5 shadow-[0_6px_0_#1F8FDB] hover:brightness-105 active:translate-y-0.5 transition"
              data-testid="hero-cta-mini"
            >
              <PuzzlePiece size={30} weight="fill" className="flex-none" />
              <span className="text-left">
                <span className="block font-display text-xl sm:text-2xl font-black tracking-tight leading-tight">Play today's puzzle</span>
                <span className="block text-sm font-semibold opacity-90">A daily brain-teaser. No sign-up.</span>
              </span>
              <ArrowRight size={22} weight="bold" className="flex-none group-hover:translate-x-0.5 transition" />
            </Link>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="btn-tile rounded-xl bg-brand hover:bg-brand-hover text-brand-foreground px-7 h-12 text-base"
              data-testid="hero-cta-register"
            >
              <Link to="/register">
                Choose your raider
                <ArrowRight size={18} className="ml-1.5" weight="bold" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="rounded-xl h-12 px-6 font-display font-extrabold text-base text-opp border-2 border-opp bg-transparent hover:bg-opp/10 hover:text-opp"
              data-testid="hero-cta-how-to-play"
            >
              <Link to="/how-to-play">
                <PlayCircle size={20} weight="fill" className="mr-1.5" />
                How to play
              </Link>
            </Button>
            <Button asChild variant="ghost" size="lg" className="rounded-xl h-12 px-6 font-semibold" data-testid="hero-cta-login">
              <Link to="/login">I already play</Link>
            </Button>
            <GuestLink />
          </div>
        </div>
      </section>

      {/* Raider line-up */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-6 pb-4">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground text-center mb-5">
          Twenty raiders. One flag.
        </p>
        <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 max-w-3xl mx-auto" aria-hidden="true">
          {CHARACTERS.map((c, i) => (
            <div
              key={c.id}
              title={c.name}
              className={`aspect-square rounded-2xl overflow-hidden flex items-end justify-center ${
                i % 2 ? "bg-opp/15" : "bg-brand/15"
              }`}
            >
              <Avatar2D character={c} className="w-[96%] -mb-[8%]" />
            </div>
          ))}
        </div>
      </section>

      {/* How it plays */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {STEPS.map((s) => (
            <div key={s.n} className="flex gap-4 border border-border rounded-2xl p-6 bg-surface">
              <Tile ch={s.n} tone={s.tone} className="w-11 h-11 text-xl flex-none" />
              <div>
                <h3 className="font-display text-xl font-bold tracking-tight">{s.title}</h3>
                <p className="text-muted-foreground mt-1 leading-relaxed">{s.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Strip */}
      <section className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Your seat is open.</h2>
            <p className="text-muted-foreground mt-2">Free forever. Play on the web, Android and iPhone.</p>
          </div>
          <Button
            asChild
            size="lg"
            className="btn-tile rounded-xl bg-brand hover:bg-brand-hover text-brand-foreground px-7 h-12 text-base"
            data-testid="footer-cta-register"
          >
            <Link to="/register">Create your account</Link>
          </Button>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 flex items-center justify-between text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} Jangle</span>
          <span className="font-display tracking-tight">Jangle · Word Raiders</span>
        </div>
      </footer>
    </main>
  );
}
